import React, { useCallback, useContext, useMemo, useState } from 'react'
import { DonutChart, DonutChartItem } from 'react-charts/dist/DonutChart';
import { avg, groupBy, sum } from 'simple-utils';
import { CepPage } from '../cep/cep-page';
import { CounterCard, MoneyCounterCard, PercentCard, useFilterActionTrigger } from '../components/CounterCard';
import { useConfirm } from '../components/Dialogs';
import { HelpModalButton } from '../help/help-modal';
import { AuthContext, useIsAwr } from '../login/AuthContext';
import { Account, BalanceKind, balanceKind, goalStatus, goalStatusColor, overrunStatus } from '../models/Account';
import { FullReportEntry } from '../models/FullReportEntry';
import { AccountContext } from './AccountContext'
import { SummaryCharts } from './charts/SummaryCharts';
import { noEntryToday } from './filtering/filters';
import { FilterAction } from './filtering/FilterAction';
import { PeriodLabel, PeriodSelectorButton } from './PeriodSelector';
import { RegionReportButton } from './region-report/region-report-modal';
import { CalcButton } from './calculator/calculator';
import { WaQueryButton } from '../wa-query/wa-query-modal';
import { useWindowWidth } from 'tooltip';

export function count<T>(list:T[], keyFn: (i: T) => string):Record<string, number>{
    let result:Record<string, number> = {};
    for(var i in list){
        let element = list[i];
        let key = keyFn(element);
        if(key in result){
            result[key] += 1;
            continue;
        }
        result[key] = 1;
    }
    return result;
}


interface ReportNumbers {
    avgLeads: number
    avgCPL: number
    avgCPM: number
    totalCost: number
    totalLeads: number
    totalExpectedLeads: number
    totalGoal: number
    numAccounts: number
    numManagers: number
    numStates: number
    badStatusCount: number
    badBalance: number
    leadWarningCount: number
    budgetWarningCount: number
    trendChart: DonutChartItem[]
    statusChart: DonutChartItem[]
    todayStatusChart: DonutChartItem[]
    goalChart: DonutChartItem[]
    noEntryToday: number
    key: number
}

function trendLabel(key: string){
    switch(key){
        case "up": return "Alta";
        case "down": return "Baixa";
        case "none": return "Estável"; 
        default: return "Sem dados";
    }
}

function trendStyle(key: string){
    switch(key){
        case "up": return "color2";
        case "down": return "color4";
        case "none": return "color3"; 
        default: return "color20";
    }
}

function statusLabel(a:Account){
    switch(a.status){
        case 0: return 'Ativa';
        case 3: return 'Período de Tolerância'
        default: return 'Com erro';
    }
}

function statusLabelToday(a:Account){
    switch(a.status){
        case 0: return "Back Online";
        case 3: return 'Período de Tolerância'
        default:  return 'Desativada';
    }
}

function statusStyle(key:string){
    switch(key){
        case 'Back Online':
        case 'Ativa': return "color2";
        case 'Período de Tolerância': return "color16";
        default: return "color4";
    }
}

var key = 1;

function getOnlyNumber(n:number){
    if(n === null) return 0;
    if(isNaN(n)) return 0;
    return n;
}

function computeBadBalance(accounts:FullReportEntry[]):number {
    var result = count(accounts, a => balanceKind(a) + "")
    return (result[BalanceKind.danger] ?? 0) + (result[BalanceKind.warning] ?? 0) 
}

function useReportNumbers(accounts:FullReportEntry[]):ReportNumbers{
    const trendChart: DonutChartItem[] = Object.entries(count(accounts, a => a.trend)).map(([key, value]) => ({label: trendLabel(key), value, style: trendStyle(key)}));
    const statusChart: DonutChartItem[] = Object.entries(count(accounts, a => statusLabel(a))).map(([key, value]) => ({label: key, value, style: statusStyle(key)}));
    const goalChart: DonutChartItem[] = Object.entries(count(accounts, a => goalStatus(a))).map(([key, value]) => ({label: key, value, style: goalStatusColor(key)}));
    const todayStatusChart: DonutChartItem[] = Object.entries(count(accounts.filter(a => a.changeStatusToday), a => statusLabelToday(a))).map(([key, value]) => ({label: key, value, style: statusStyle(key)}));

    return useMemo(()=>{
        key++;
        return {
            numAccounts: accounts.length,
            numStates: Object.keys(groupBy(accounts, a => a.state)).length,
            numManagers: Object.keys(groupBy(accounts, a => a.manager)).length,
            budgetWarningCount: accounts.filter(a => overrunStatus(a) != 'Neutro').length,
            leadWarningCount: accounts.filter(a => a.nLeads < a.expectedLeads).length,
            badStatusCount: accounts.filter(a => a.status != 0).length,
            trendChart,
            statusChart,
            todayStatusChart,
            goalChart,
            noEntryToday: accounts.filter(noEntryToday).length,
            totalCost: sum(accounts, a => a.totalCost),
            totalGoal: sum(accounts, a => a.goal),
            totalLeads: sum(accounts, a => a.nLeads),
            totalExpectedLeads: sum(accounts, a => a.expectedLeads),
            avgLeads: avg(accounts, a => a.nLeads),
            avgCPL: avg(accounts, a => a.nLeads ? a.totalCost / a.nLeads : 0),
            avgCPM: avg(accounts, a => getOnlyNumber(a.totalCost / a.impressions * 1000)),
            badBalance: computeBadBalance(accounts),
            key
        }

    }, [accounts])
}


function baseTooltip(item:DonutChartItem){
    return <>
        {item.label}: {item.value}
    </>
}

function SwithReport(){
    const isGoogle = useMemo(function(){return location.pathname.match(/google/)}, [])
    return <a className="btn btn-primary" href={`${isGoogle ? '/reports' : '/google-reports'}`}><i className={`me-1 fab ${isGoogle ? 'fa-facebook' : 'fa-google'}`}></i>Ver Painel do {isGoogle ? 'Facebook' : 'Google'}</a>
    
}

export function WelcomeScreen(){
    const [mainPage, setMainPage] = useState("");
    const {filtered, accounts, period, dispatch} = useContext(AccountContext);
    const {user} = useContext(AuthContext);

    const report = useReportNumbers(filtered || accounts);
    const badStatusClick = useFilterActionTrigger(FilterAction.badStatus, "status-da-conta")
    const badBalanceClick = useFilterActionTrigger(FilterAction.badBalance, "saldo")
    const noInfoClick = useFilterActionTrigger(FilterAction.noInfo, "status-da-conta")
    const overrunClick = useCallback(()=>{
        dispatch({type: FilterAction.clearFilters});
        dispatch({type: FilterAction.setOverrun, payload: "Alto ou Baixo"});
        dispatch({type: "setSorter", payload: {id: "investimento-orcamento", order: "desc"}});
    }, [])

    const isAwr = useIsAwr();
    const width = useWindowWidth();

    if(mainPage == 'cep') return <CepPage back={() => setMainPage("")} />
 
    return <>
        <div className='d-flex align-items-start justify-content-between gap-2'>
            <h2 className='mb-4'>Bem-vindo, <small>{user.name}</small></h2>
            {isAwr && width > 1000 && <SwithReport />}
        </div>
        <PeriodLabel />

        <div className='counter-cards'>
            <CounterCard title='Contas' count={report.numAccounts} />
            <CounterCard title='Estados' count={report.numStates - (report.numStates > 20 ?  2 : 0)} />
            <CounterCard title='Gestores' count={report.numManagers} />
            <CounterCard onClick={badStatusClick} title={<><i className='fa fa-exclamation-triangle'></i> Bad Status</>} count={report.badStatusCount} />
            {period == null && <CounterCard onClick={noInfoClick} title={<><i className='fa fa-exclamation-triangle'></i> No Info</>} count={report.noEntryToday} />}
            <CounterCard title={<><i className='fa fa-exclamation-triangle'></i> Leads</>} count={report.leadWarningCount} />
            <CounterCard title={<><i className='fa fa-exclamation-triangle'></i> Orçamento</>} onClick={overrunClick} count={report.budgetWarningCount} />
            {report.badBalance > 0 && <CounterCard  onClick={badBalanceClick} title={<><i className='fa fa-exclamation-triangle'></i> Saldo</>} count={report.badBalance} />}
        </div>

        <div className='row'>
            <div className='mb-3 col-lg'>
                <h3>Status</h3>
                <DonutChart key={report.key} className='d-flex align-items-center' data={report.statusChart} height={170} width={180} radius={50} thickness={16} textRadiusOffset={70} tooltipContainer=".detail-div" createTooltip={baseTooltip} />
            </div>
            {report.todayStatusChart.length > 0 && <div className='mb-3 col-lg'>
                <h3>Status Hoje</h3>
                <DonutChart key={report.key} className='d-flex align-items-center' data={report.todayStatusChart} height={170} width={180} radius={50} thickness={16} textRadiusOffset={70} tooltipContainer=".detail-div" createTooltip={baseTooltip} />
            </div>}
            <div className='mb-3 col-lg'>
                <h3>Tendências</h3>
                <DonutChart key={report.key} className='d-flex align-items-center' data={report.trendChart} height={170} width={180} radius={50} thickness={16} textRadiusOffset={70}  tooltipContainer=".detail-div" createTooltip={baseTooltip} />
            </div>
            <div className='mb-3 col-lg'>
                <h3>Meta</h3>
                <DonutChart key={report.key} className='d-flex align-items-center' data={report.goalChart} height={170} width={180} radius={50} thickness={16} textRadiusOffset={70}  tooltipContainer=".detail-div" createTooltip={baseTooltip} />
            </div>
        </div>

        <SummaryCharts />

        {user.id == 1 && <>
        <h3 className='mt-3'>Médias</h3>
        <div className='counter-cards'>
            <CounterCard title='Leads' count={Math.round(report.avgLeads)} />
            <MoneyCounterCard title='CPL' count={Math.round(report.avgCPL)} />
            <MoneyCounterCard title='CPM' count={Math.round(report.avgCPM)} />
        </div> </> }

        <h3 className='mt-3'>Totais</h3>
        <div className='counter-cards'>
            <CounterCard title='Total de Leads' count={Math.round(report.totalLeads)} />
            {period == null && <CounterCard title={'Leads Esperados'} count={Math.round(report.totalExpectedLeads)} />}
            <LeadsToGoal current={period == null} goal={report.totalGoal} expectedLeads={report.totalExpectedLeads} total={report.totalLeads} />
            <CounterCard title='Meta Total' count={Math.round(report.totalGoal)} />
            
            {report.totalGoal != 0 && <PercentCard title='Meta Performance' ratio={report.totalLeads / report.totalGoal} />}
            {period == null && report.totalGoal != 0 && <PercentCard title='Meta Proporcional' ratio={report.totalLeads / report.totalExpectedLeads} />}
            <MoneyCounterCard title='Investimento' count={report.totalCost} />
        </div>

        <h3 className='mt-3'>Ferramentas</h3>

        <div className="">
            <a className='btn btn-primary me-2 mb-2' onClick={() => setMainPage("cep")}><i className="fas fa-map-marked-alt"></i> Buscar CEPs</a>
            <a className='btn btn-primary me-2 mb-2' onClick={() => dispatch({type:"setTableMode", payload: true})}><i className="fas fa-table"></i> Modo Tabela</a>
            <a className='btn btn-primary me-2 mb-2' onClick={() => dispatch({type:"setChannelMode", payload: true})}><i className="fas fa-table-columns"></i> Modo Canais</a>
            <PeriodSelectorButton />
            <RegionReportButton />
            <CalcButton />
            <HelpModalButton />
            <WaQueryButton />
            <LogoutButton />
        </div>
       
    </>
}

function LeadsToGoal({total, expectedLeads, goal, current}:{total:number, expectedLeads:number, goal:number, current:boolean}){
    const value = expectedLeads - total;
    if (value < 1)
        return <CounterCard title='Leads Acima' count={Math.abs(value)} />
    return <CounterCard title='Leads Abaixo' count={value} />
}

export function LogoutButton(){
    const confirm = useConfirm(<>Deseja sair?</>, {})

    async function logout(){
        if(await confirm()){
            localStorage.removeItem('token')
            location.reload();
        }
    }

    return <button className="btn btn-primary me-2 mb-2" onClick={logout}><i className="fa fa-person-walking-arrow-right me-2"></i>Sair</button>
}